import * as React from 'react';
import styled from 'styled-components';
import dayjs from 'dayjs';
import dayjsDuration from 'dayjs/plugin/duration';

dayjs.extend(dayjsDuration);

// Components
import {
  ToolButton,
  parseRecurrenceString,
  RECURRENCE_TYPES,
  Chip,
  ActionMenu
} from '@ibinder/common-frontend';

// Utils
import { extractPropValueOfType, getFormatedDate } from '@ibinder/common-utils';

// Icons
import {
  chevronUp,
  chevronDown,
  fileTypeDefault,
  edit,
  search,
  magnifyCheck,
  check,
  eyeOpened,
  eyeClosed
} from '@ibinder/common-icons';
import { ColumnEnum, GROUP_TYPES } from '../GlobalWorkflowTemplates/transformation';
import { ThemeChip, Flexbox, AvatarSizesEnum, ThemeAvatar} from '@ibinder/ui-common-core';
import { chipSizes, chipTypes } from '@ibinder/ui-common-styles';

// Styles
export const BoldSpan = styled.span`
  font-weight: ${({ theme }) => theme.fontStyles.smallBoldFont.fontWeight};
  font-size: ${({ theme }) => theme.fontStyles.smallBoldFont.fontSize};
  white-space: nowrap;
`;

const FakeButton = styled.div`
  cursor: pointer;
  color: ${({ theme }) => theme.link.colors.primary};
  display: flex;
  align-items: center;
  width: calc(100% - 3rem);

  > svg {
    min-width: 36px;
    max-width: 36px;
  }

  > span {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    max-width: 100%;
  }
`;

const Documents = styled.div`
  display: flex;

  & > button {
    margin-right: 0.25rem;

    &:first-child {
      padding-left: 0.75rem;
    }
  }
`;

const NameWrapper = styled.div`
  display: flex;
  cursor: pointer;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

// Source of truth for both headers and item transformations
export const globalInspectionTemplateMdMap = (canUseInspectionIssuesWithWorkflow:boolean) => {
 const templates = [
  {
  id: 'Name',
  name: 'Name',
  propName: 'name'
},
{
  id: 'Market',
  name: 'Market',
  propName: 'market'
},
{
  id: 'Category',
  name: 'Category',
  propName: 'category'
},
{
  id: 'Recurs',
  name: 'Recurs',
  propName: 'inspectionInterval'
},
{
  id: 'Reminder',
  name: 'Reminder',
  propName: 'remindBeforeDueDate'
},
{
  id: 'Documents',
  name: 'Documents',
  propName: 'allDocuments'
},
{
  id: 'AppAndPropertyCompatible',
  name: 'App & property',
  propName: 'appAndPropertyCompatible'
}]

// Conditionally push the 'Template' object if `canUseInspectionIssuesWithWorkflow` is true
if (canUseInspectionIssuesWithWorkflow) {
  templates.splice(3, 0, {
    id: 'Template',
    name: 'Workflow Template',
    propName: 'workflowTemplateId'
  }); // `3` is the index of the 4th place
}

return templates;

};
export const globalWorkflowTemplateMdMap = (canUseInspectionIssuesWithWorkflow:boolean) => {
  return [
    {
      id: ColumnEnum.name,
      name: 'Name',
      propName: ColumnEnum.name
    },
    {
      id: ColumnEnum.market,
      name: 'Market',
      propName: ColumnEnum.market
    },
    {
      id: ColumnEnum.isVisible,
      name: 'Showing',
      propName: ColumnEnum.isVisible
    },  
    {
      id: ColumnEnum.created,
      name: 'Created On',
      propName: ColumnEnum.created
    },
    {
      id: ColumnEnum.createdBy,
      name: 'Created By',
      propName: ColumnEnum.createdBy
    },
    {
      id: ColumnEnum.updated,
      name:'Last Edit',
      propName: ColumnEnum.updated
    },
    {
      id: ColumnEnum.lastUpdatedBy,
      name: 'Last Edit By',
      propName: ColumnEnum.lastUpdatedBy
    }
  ];
};

// Recurrence Options
export const getRecurrenceUnitOptions = (t) => [
  {
    Key: RECURRENCE_TYPES.day,
    Value: t('day(s)')
  },
  {
    Key: RECURRENCE_TYPES.week,
    Value: t('week(s)')
  },
  {
    Key: RECURRENCE_TYPES.month,
    Value: t('month(s)')
  },
  {
    Key: RECURRENCE_TYPES.year,
    Value: t('year(s)')
  }
];

// mdMap = meta data map
export const transformGlobalInspectionTemplate = (mdMap) => (template) => ({
  ...template,
  metadata: [
    ...mdMap.map(({ id: metadataElementId, propName, type }) => ({
      metadataElementId,
      value:
        metadataElementId === 'AppAndPropertyCompatible'
          ? ['appCompatible', 'propertyStructureCompatible'].every(
              (prop) => !!template[prop]
            )
          : extractPropValueOfType(type, template[propName] || '')
    }))
  ]
});

export const transformGlobalInspectionTemplateList = (list,canUseInspectionIssuesWithWorkflow) =>
  list.map(transformGlobalInspectionTemplate(globalInspectionTemplateMdMap(canUseInspectionIssuesWithWorkflow)));

export const transformGlobalWorkflowTemplateList = (list,canUseInspectionIssuesWithWorkflow) =>
  list.map(globalWorkflowTemplateMdMap(canUseInspectionIssuesWithWorkflow));
export const inspectionTypeName = (onActionSelect, t) => (name, itemId) =>
  (
    <FakeButton
      onClick={(e) => {
        e.stopPropagation();
        onActionSelect('view', itemId);
      }}
    >
      {magnifyCheck}
      <span>{name}</span>
    </FakeButton>
  );

  export const GlobalTemplateName = (onActionSelect) => (name, itemId) =>
  (
    <NameWrapper
      onClick={(e) => {
        e.stopPropagation();
        onActionSelect('view', itemId);
      }}
    >
      <span>{name}</span>
    </NameWrapper>
  );
export const actionMenu = (onActionSelect, t) => (itemId) =>
  (
    <ActionMenu
      placement="right-start"
      actions={[
        {
          icon: edit,
          action: 'edit'
        }
      ]}
      onSelect={(action) => onActionSelect(action, itemId)}
      labels={{
        identityFunction: (n) => t(n)
      }}
    />
  );

export const recurrenceOutput = (t) => (value) => {
  if(!value) return '-';
  const recurrenceUnitOptions = getRecurrenceUnitOptions(t);
  const recurrence = parseRecurrenceString(value);
  const recurrenceUnit = recurrenceUnitOptions.find(
    ({ Key }) => Key === recurrence.designator
  );
  return <p>{`${recurrence.value} ${recurrenceUnit.Value}`}</p>;
};

export const documentChips = (documents) => (
  <Documents>
    <Chip label={'§ ' + documents.leniencyDocuments.length} />
    <Chip label={documents.additionalDocuments.length} icon={fileTypeDefault} />
  </Documents>
);

export const getHeaders = (t, onActionSelect,workflowTemplateList,canUseInspectionIssuesWithWorkflow) =>
  globalInspectionTemplateMdMap(canUseInspectionIssuesWithWorkflow).map((item) => {
    switch (item.id) {
      case 'Name':
        return {
          ...item,
          Aggregated: (p) => (
            <ToolButton type="button" onClick={() => ({})}>
              {p.row.isExpanded ? chevronUp : chevronDown}
              <BoldSpan>{p.row.groupByVal}</BoldSpan>
            </ToolButton>
          ),
          Cell: (cell) => {
            const itemId = cell.data[cell.row.index].docId;
            return (
              <NameWrapper>
                {inspectionTypeName(onActionSelect, t)(cell.value, itemId)}
                {actionMenu(onActionSelect, t)(itemId)}
              </NameWrapper>
            );
          },
          isUnwrapped: true
        };
        case 'Template':
          return {
            ...item,
            Cell: (cell) => {
              if (cell.value) {
                const templateName = workflowTemplateList.find(
                  (workflowTemplate) => cell.value === workflowTemplate.Key
                )?.Value;
                return templateName;
              } else {
                return '-';
              }
            }
        };
      case 'Recurs':
      case 'Reminder':
        return {
          ...item,
          Cell: (cell) => {
            if (cell.value) {
              return recurrenceOutput(t)(cell.value);
            }
            return '';
          }
        };
      case 'Documents':
        return {
          ...item,
          Cell: (cell) => {
            if (cell.value) {
              return documentChips(cell.value);
            }
            return '';
          }
        };
      case 'AppAndPropertyCompatible': {
        return {
          ...item,
          Cell: (cell) => {
            if (cell.value === true) {
              return check;
            }
            return '';
          }
        };
      }
      default:
        return item;
    }
  });
  export const renderUserName = (createdBy: string) => {
    return (
      <Documents>
      <ThemeChip
        noPadding={true}
        variant={chipTypes.Neutral}
        size={chipSizes.Avatar}
      >
        {createdBy &&(<Flexbox alignItems='center'>
          <ThemeAvatar fullName={createdBy} size={AvatarSizesEnum.small} />
          <span style={{ marginLeft: '0.5rem' }}>{createdBy}</span>
        </Flexbox>)}
      </ThemeChip>
      </Documents>
    );
  };

  export const getFormatedDateColoumn = (data) => (
    data ? getFormatedDate(data) : ''
  );

  export const getGloabalWorkflowTemplateHeaders = (t, onActionSelect,workflowTemplateList,canUseInspectionIssuesWithWorkflow, handleWorkflowVisibility) =>
    globalWorkflowTemplateMdMap(canUseInspectionIssuesWithWorkflow).map((item) => {
      switch (item.id) {
        case 'name':
          return {
            ...item,
            Aggregated: (p) => (
              <ToolButton type="button" onClick={() => ({})}>
                {p.row.isExpanded ? chevronUp : chevronDown}
                <BoldSpan>{p.row.groupByVal}</BoldSpan>
              </ToolButton>
            ),
            Cell: (cell) => {
              return (
                <NameWrapper>
                  {GlobalTemplateName(onActionSelect)(cell?.row?.original?.document?.name, cell?.row?.original?.document?.id)}
                </NameWrapper>
              );
            },
            isUnwrapped: true
          };
          case 'isVisible':
          return {
            ...item,
            Cell: (cell) => {
              return (
                cell?.row?.original?.document?.isVisible !== undefined && 
                <FakeButton
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      handleWorkflowVisibility(cell.row.original.docId);
                    }}
                  >
                    {cell?.row?.original?.document?.isVisible === true ? eyeOpened : eyeClosed}
                  </FakeButton>
              )
            }
          }; 
          case 'created':
          return {
            ...item,
            Cell: (cell) => {
              return  getFormatedDateColoumn(cell?.row?.original?.document?.created);
            }
          };
          case 'createdBy':
          return {
            ...item,
            Cell: (cell) => {
              return renderUserName(cell?.row?.original?.document?.createdBy?.name);
            }
          };
          case 'lastUpdatedBy':
          return {
            ...item,
            Cell: (cell) => {
              return renderUserName(cell?.row?.original?.document?.lastUpdatedBy?.name);
            }
          };
          case 'updated':
          return {
            ...item,
            Cell: (cell) => {
              return getFormatedDateColoumn(cell?.row?.original?.document?.updated);
            }
          };
          case 'market':
          return {
            ...item,
            Cell: (cell) => {
              return cell?.row?.original?.document?.market
            }
          };
      
        default:
          return item;
      }
    });

export const getGroupsFromAccessor = (list, accessor) =>
  list
    .map((item) => item[accessor])
    .filter((val, i, self) => self.indexOf(val) === i)
    .map((item) => ({ group: item, isNew: false }));

  export const mapQueryToTableData = (list, markets) =>
      list.map((item) => ({
        ...item,
        id: item.id,
        name: item.name,
        description: item.description,
        market: markets.find(({ country }) => country.countryCode === item.market)
          ?.name,
        category: item.inspectionCategory.name,
        workflowTemplateId: item.workflowTemplateId,
        inspectionInterval: item.showRecurrenceOptions
          ? item.inspectionInterval
          : null,
        remindBeforeDueDate: item.showReminderOptions
          ? item.remindBeforeDueDate
          : null,
        allDocuments: {
          leniencyDocuments: item.leniencyDocuments,
          additionalDocuments: item.additionalDocuments
        },
        appAndPropertyCompatible:
          item.appCompatible && item.propertyStructureCompatible
    }));

export const mapQueryToTableDataGlobalWorkflowTemplate = (list, markets, tabTypeWorkflows = []) =>
  list.map((item) => ({
    ...item,
    id: item.id,
    name: item.name,
    isVisible: item.isVisible,
    created: item.created,
    createdBy: item.createdBy,
    updated: item.updated,
    lastUpdatedBy: item.lastUpdatedBy,  
    market: item.market === 'All' ? 'All' : markets?.find(({ country }) => country.countryCode === item.market)
    ?.name,
    category: tabTypeWorkflows.includes(item.name) ? GROUP_TYPES.TabTypes : GROUP_TYPES.Workflows
  }));


const countLengthOfArrays = (obj) =>
  Object.values(obj)
    .map((arr: []) => arr.length)
    .reduce((prev, next) => prev + next, 0);

export const sortTemplates = (list, prop, asc) => {
  const sortedList = [...list];
  const isInterval =
    ['inspectionInterval', 'remindBeforeDueDate'].indexOf(prop) > -1;
  const isDoc = prop === 'allDocuments';

  sortedList.sort((first, second) => {
    let a, b;

    if (isInterval) {
      a = dayjs.duration(first[prop] || '').asMilliseconds() || 0;
      b = dayjs.duration(second[prop] || '').asMilliseconds() || 0;
    } else if (isDoc) {
      a = countLengthOfArrays(first[prop]);
      b = countLengthOfArrays(second[prop]);
    } else {
      a = first[prop]?.toString()?.toLowerCase() || '';
      b = second[prop]?.toString()?.toLowerCase() || '';
    }

    if (a > b) {
      return asc ? 1 : -1;
    } else {
      return asc ? -1 : 1;
    }
  });
  return sortedList;
};
